import React from 'react';
import { View } from 'react-native';
import { Check } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import type { WorkoutSetSequenceItem } from '~/workout/hooks/useWorkoutSetSequence';
import { WorkoutSetValue } from '~/workout/components/workout-sets/WorkoutSetValue';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type {
  MuscleTargetRecord,
  WorkoutExerciseDimensionRecord,
} from '~/powersync/generated/schema';

interface ActiveSetLabel {
  /** 1-based position of this set among all sets */
  index: number;
  /** Total number of sets in the sequence */
  total: number;
}

interface ActiveSetProps {
  /** The selected set item driving the card */
  setItem: WorkoutSetSequenceItem;
  /** The workout ID */
  workoutId: string;
  /** Exercise dimensions for the selected set's exercise */
  dimensions?: WorkoutExerciseDimensionRecord[];
  /** Muscle targets for the exercise */
  muscleTargets?: MuscleTargetRecord[];
  /** "SET x/y" label values */
  label: ActiveSetLabel;
  /** Callback when the complete button is pressed */
  onCompleteSet?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export const ActiveSet: React.FC<ActiveSetProps> & {
  Synced: typeof ActiveSetSynced;
} = ({
  setItem,
  workoutId,
  dimensions,
  muscleTargets,
  label,
  onCompleteSet,
  className,
}) => {
  const { t } = useTranslation('workout');
  const localizedName = useLocalizedValue(setItem.sportActivity.name);
  const isCompleted = Boolean(setItem.set.completedAt);

  const muscleLine = muscleTargets
    ?.map((target) => target.muscleGroup)
    .join(' • ');

  return (
    <View className={className}>
      <View className="gap-4">
        {/* Header: exercise + SET x/y */}
        <View className="flex-row items-start justify-between gap-2">
          <View className="flex-1 gap-1">
            <Text className="font-sora-semibold text-xl text-text-primary">
              {localizedName}
            </Text>
            {!!muscleLine && (
              <Text className="text-sm capitalize text-text-secondary">
                {muscleLine}
              </Text>
            )}
          </View>
          <View className="items-end">
            <Text className="text-[10px] font-semibold text-text-secondary">
              {t('progress.setBadge', 'SET')}
            </Text>
            <Text className="font-sora-bold text-2xl text-accent">
              {label.index}/{label.total}
            </Text>
          </View>
        </View>

        {/* Inputs */}
        <View className="flex-row gap-3">
          {dimensions?.map((dimension) => (
            <View key={dimension.id} className="flex-1 gap-1.5">
              <Text className="text-xs font-medium capitalize text-text-secondary">
                {dimension.dimension}
                {dimension.unit ? ` (${dimension.unit})` : ''}
              </Text>
              <WorkoutSetValue.Synced
                setId={setItem.set.id}
                exerciseDimensionId={dimension.id}
                workoutId={workoutId}
              />
            </View>
          ))}
        </View>

        {/* Complete */}
        <Button
          onPress={onCompleteSet}
          disabled={isCompleted}
          className={cn(
            'flex-row items-center justify-center gap-2',
            isCompleted ? 'bg-surface-elevated' : 'bg-accent',
          )}
        >
          <Icon
            as={Check}
            size={18}
            className={isCompleted ? 'text-text-muted' : 'text-accent-text'}
          />
          <Text
            className={cn(
              'font-medium',
              isCompleted ? 'text-text-muted' : 'text-accent-text',
            )}
          >
            {t('sequence.completeSet', 'Complete Set')}
          </Text>
        </Button>
      </View>
    </View>
  );
};

interface ActiveSetSyncedProps {
  /** The selected set item driving the card */
  setItem: WorkoutSetSequenceItem;
  /** The workout ID */
  workoutId: string;
  /** "SET x/y" label values */
  label: ActiveSetLabel;
  /** Callback when the complete button is pressed */
  onCompleteSet?: () => void;
  /** Additional CSS classes */
  className?: string;
}

const ActiveSetSynced: React.FC<ActiveSetSyncedProps> = ({
  setItem,
  workoutId,
  label,
  onCompleteSet,
  className,
}) => {
  // Fetch muscle targets for the current exercise's sport activity
  const { data: muscleTargets } = useLivePowerSyncQuery<MuscleTargetRecord>(
    db.SportActivity.queries.findMuscleTargets(setItem.sportActivity.id),
    [setItem.sportActivity.id],
  );

  // Fetch the exercise dimensions to render labeled inputs
  const { data: dimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      db.Workout.queries.findDimensionsByExercise(setItem.exercise.id),
      [setItem.exercise.id],
    );

  return (
    <ActiveSet
      setItem={setItem}
      workoutId={workoutId}
      dimensions={dimensions}
      muscleTargets={muscleTargets}
      label={label}
      onCompleteSet={onCompleteSet}
      className={className}
    />
  );
};

// Attach Synced as a static property
ActiveSet.Synced = ActiveSetSynced;
